import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Trash2, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtDate, telLink, waLink } from "@/lib/api";
import { PageHeader, Panel, DarkBtn, ConfirmDelete, STATUS } from "@/components/admin/AdminUI";

const exportCsv = (rows) => {
  const head = ["Ad Soyad", "Telefon", "E-posta", "Tarih", "Saat", "Kişi", "Not", "Durum", "Pazarlama İzni", "Oluşturulma"];
  const lines = rows.map((r) => [r.name, r.phone, r.email, r.date, r.time, r.guests, r.note, STATUS[r.status]?.[0], r.marketing_consent ? "Evet" : "Hayır", r.created_at]
    .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";"));
  const blob = new Blob(["\ufeff" + [head.join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "rezervasyonlar.csv";
  a.click();
};

export default function Reservations() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");

  const load = () => api.get("/admin/reservations", { params: { status: status || undefined, date: date || undefined } }).then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, date]);

  const setSt = async (id, s) => {
    try {
      await api.patch(`/admin/reservations/${id}`, { status: s });
      toast.success("Durum güncellendi");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (id) => {
    await api.delete(`/admin/reservations/${id}`);
    toast.success("Rezervasyon silindi");
    load();
  };

  return (
    <div data-testid="admin-reservations-page">
      <PageHeader title="Rezervasyonlar" desc="Siteden gelen masa talepleri. Müşteriyi arayıp teyit ettikten sonra onaylayın.">
        <DarkBtn onClick={() => exportCsv(rows)} data-testid="admin-reservations-export-btn"><Download size={16} /> CSV İndir</DarkBtn>
      </PageHeader>
      <Panel className="p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex flex-wrap gap-2">
          {[["", "Tümü"], ...Object.entries(STATUS).map(([k, v]) => [k, v[0]])].map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} className={`rounded-full px-3 py-1.5 text-xs border ${status === k ? "bg-[#1A1513] text-[#D4AF37] border-[#1A1513]" : "border-[#E5DCD0]"}`} data-testid={`admin-reservations-filter-${k || "all"}`}>{l}</button>
          ))}
        </div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" data-testid="admin-reservations-date-filter" />
        {date && <button onClick={() => setDate("")} className="text-xs text-[#C84B31]" data-testid="admin-reservations-clear-date">Tarihi temizle</button>}
      </Panel>
      <div className="space-y-3">
        {!rows.length && <Panel className="p-10 text-center text-sm text-[#6B5E56]" >Rezervasyon bulunamadı.</Panel>}
        {rows.map((r) => (
          <Panel key={r.id} className="p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`admin-reservation-row-${r.id}`}>
            <div className="md:col-span-4">
              <p className="font-semibold text-lg">{r.name}</p>
              <p className="text-xs text-[#6B5E56]">{r.phone}{r.email && ` · ${r.email}`}</p>
              {r.marketing_consent && <span className="mt-1 inline-block text-[10px] rounded-full bg-[#F3EDE2] px-2 py-0.5">Ticari ileti izni var</span>}
              {r.notify_ok !== undefined && (
                <span className={`mt-1 ml-1 inline-block text-[10px] rounded-full px-2 py-0.5 ${r.notify_ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} title={r.notify_results?.[0]?.error || ""} data-testid={`admin-reservation-notify-${r.id}`}>
                  {r.notify_ok ? "WhatsApp bildirimi gitti" : "WhatsApp bildirimi gitmedi"}
                </span>
              )}
            </div>
            <div className="md:col-span-3">
              <p className="font-display">{r.date} · {r.time}</p>
              <p className="text-xs text-[#6B5E56]">{r.guests} kişi · Talep: {fmtDate(r.created_at)}</p>
              {r.note && <p className="text-xs mt-1 italic text-[#6B5E56]">"{r.note}"</p>}
            </div>
            <div className="md:col-span-3">
              <select value={r.status} onChange={(e) => setSt(r.id, e.target.value)} className={`rounded-full px-3 py-1.5 text-xs font-semibold border-0 ${STATUS[r.status]?.[1]}`} data-testid={`admin-reservation-status-${r.id}`}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 flex md:justify-end gap-1">
              <a href={telLink(r.phone)} className="p-2 rounded-md hover:bg-[#F3EDE2]" aria-label="Ara" data-testid={`admin-reservation-call-${r.id}`}><Phone size={15} /></a>
              <a href={waLink(r.phone.startsWith("0") ? "9" + r.phone : r.phone, `Merhaba ${r.name}, Yeni Mercan Restoran ${r.date} ${r.time} rezervasyonunuz hakkında yazıyoruz.`)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-md hover:bg-[#F3EDE2]" aria-label="WhatsApp" data-testid={`admin-reservation-wa-${r.id}`}><MessageCircle size={15} /></a>
              <ConfirmDelete onConfirm={() => remove(r.id)}>
                <button className="p-2 rounded-md hover:bg-red-50 text-[#C84B31]" aria-label="Sil" data-testid={`admin-reservation-delete-${r.id}`}><Trash2 size={15} /></button>
              </ConfirmDelete>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
